import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EntryCard from "@/components/feature/EntryCard";
import EntryTimeline from "@/pages/entry/components/EntryTimeline";
import EntrySidebar from "@/pages/entry/components/EntrySidebar";
import { getCategoryById, getEntryById, getRelatedEntries } from "@/hooks/useSiteData";
import { useSEO, SITE_ORIGIN } from "@/hooks/useSEO";

export default function EntryDetail() {
  const { entryId } = useParams<{ entryId: string }>();
  const entry = entryId ? getEntryById(entryId) : undefined;
  const category = entry ? getCategoryById(entry.categoryId) : undefined;
  const related = entry ? getRelatedEntries(entry, 3) : [];

  useSEO({
    title: entry ? `${entry.title} - 黑料百科` : "词条不存在 - 黑料百科",
    description: entry
      ? `${entry.summary}（黑料百科 ${entry.name} 词条，含事件时间线与公开信息整理）`
      : "该词条不存在或已合并，请返回首页浏览其他人物档案。",
    keywords: entry
      ? [entry.name, ...entry.tags, category?.name ?? "词条"].join(",")
      : "黑料百科,人物词条",
    path: entry ? `/entry/${entry.id}` : "/",
    type: "article",
    jsonLd: entry
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: entry.title,
            description: entry.summary,
            author: { "@type": "Organization", name: "黑料百科" },
            publisher: { "@type": "Organization", name: "黑料百科" },
            datePublished: entry.publishedAt,
            dateModified: entry.updatedAt,
            inLanguage: "zh-CN",
            articleSection: category?.name,
            keywords: entry.tags.join(","),
            mainEntityOfPage: `${SITE_ORIGIN}/entry/${entry.id}`,
            image: entry.cover,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
              {
                "@type": "ListItem",
                position: 2,
                name: category?.name ?? "词条",
                item: `${SITE_ORIGIN}/category/${entry.categoryId}`,
              },
              { "@type": "ListItem", position: 3, name: entry.title, item: `${SITE_ORIGIN}/entry/${entry.id}` },
            ],
          },
        ]
      : [],
  });

  if (!entry) {
    return (
      <SiteLayout>
        <div className="px-4 py-24 text-center md:px-6">
          <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这个词条</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已被合并或更名，试试从榜单里找找看。</p>
          <Link
            to="/ranking"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回热度榜单
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100/60 px-4 py-4 md:px-6">
        <Breadcrumb
          items={[
            { label: category?.name ?? "词条", path: `/category/${entry.categoryId}` },
            { label: entry.name },
          ]}
        />
      </div>

      <article className="px-4 py-10 md:px-6 md:py-12">
        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Link
              to={`/category/${entry.categoryId}`}
              className="rounded-full bg-accent-100 px-3 py-1 text-accent-800 transition-colors hover:bg-accent-200"
            >
              {category?.name}
            </Link>
            <span className="rounded-full border border-background-300 px-3 py-1 text-foreground-600">
              {entry.status}
            </span>
            <span className="text-foreground-500">首发 {entry.publishedAt}</span>
            <span className="text-foreground-500">· 更新于 {entry.updatedAt}</span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {entry.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {entry.summary}
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            {entry.tags.map((tag) => (
              <span key={tag} className="rounded bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
                #{tag}
              </span>
            ))}
          </div>
        </header>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            <figure className="overflow-hidden rounded-lg border border-background-200">
              <img
                src={entry.cover}
                alt={`${entry.name} · ${entry.title} 话题现场配图`}
                title={`${entry.name} 词条配图`}
                className="h-56 w-full object-cover object-top md:h-80"
              />
              <figcaption className="bg-background-100/70 px-4 py-2.5 text-xs text-foreground-500">
                配图为主题氛围图，与词条人物无直接关联。
              </figcaption>
            </figure>

            <section className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950">词条概述</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700 md:text-base">{entry.intro}</p>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950">事件时间线</h2>
              <p className="mt-2 text-sm text-foreground-500">
                按公开信息出现的时间顺序排列，共 {entry.timeline.length} 个节点。
              </p>
              <EntryTimeline timeline={entry.timeline} />
            </section>

            {related.length > 0 && (
              <section className="mt-12 border-t border-background-200 pt-8">
                <h2 className="font-heading text-xl font-bold text-foreground-950">相关词条</h2>
                <p className="mt-2 text-sm text-foreground-500">和这个话题经常被一起提起的档案。</p>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {related.map((item) => (
                    <EntryCard key={item.id} entry={item} />
                  ))}
                </div>
              </section>
            )}
          </div>

          <EntrySidebar entry={entry} />
        </div>
      </article>
    </SiteLayout>
  );
}